import Stepper from '@/components/Stepper';
import ToolBar from '@/components/ToolBar';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormProvider, useForm } from 'react-hook-form';
import { PeClientDetails } from './steppers/PeClientDetails';
import { isPrepaidFeatureEnabled, PeCaseDetails } from './steppers/PeCaseDetails';
import PeWoDetails, { WorkOrderSchema } from './steppers/PeWoDetails';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { displayFullName, genUniqueId } from '@/lib/utils';
import {
  ClientDetailsSchema,
  omitFormOnlyWorkOrderFields,
  PeCaseDetailsSchema,
  PeWoDetailsSchema,
  WorkOrderServeTo,
} from './pe.schema';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { getPhoneNumberValue } from '@/components/FormFieldPhone';
import Loader from '@/components/Loader';
import AlertBox from '@/components/AlertBox';
import { NotFoundRedirectionWrapper } from '@/components/NotFoundRedirectWrapper';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import {
  useCreateServiceSession,
  useEndServiceSession,
  useGetServiceById,
  useGetServiceSession,
  useRefreshServiceSession,
  useUpdateServiceById,
} from '@/services/pe';
import { useCreateHIWorkOrder, useUpdateHIWorkOrder } from '@/services/homeInspectionService';
import { ServiceCreateUpdateRequest } from '@/types/service.types';
import { Action, AfterAction, PaymentType, ProcessStatus, WorkOrderPayment } from '@/types/workOrder.types';
import { caseTypesWithWitnessFees } from '@/utils/constant';
import { hasVendorAssignmentOnWorkOrder } from '@/pages/caseManagement/processBilling/processBillingVendorUtils';
import { UserResponse } from '@/types/workOrderAttempt.types';
import { useUserStore } from '@/stores/user';
import { mapPsFormToHiCreatePayload, mapPsFormToHiUpdatePayload } from './mapPsFormToHiCreatePayload';
import { canCreateLinkedHomeInspection } from './canCreateLinkedHomeInspection';
import {
  applyLinkedHiWorkOrderIds,
  fingerprintHiCreatePayload,
  fingerprintHiUpdatePayload,
  type PendingLinkedHiByIndex,
  shouldReusePendingLinkedHi,
} from './psLinkedHiSave';
import { planPsEditLinkedHiSave } from './planPsEditLinkedHiSave';
import { getPsSaveErrorMessage } from './getPsSaveErrorMessage';
import { PsSaveValidationMessage } from './PsSaveValidationMessage';

type PsUpdateFormWorkOrder = ServiceCreateUpdateRequest['workOrders'][number] & {
  includeHomeInspection?: boolean;
  includeHomeInspectionManualOverride?: boolean;
  sameAsPropertyAddress?: boolean;
  linkedHIWorkOrderID?: string | null;
  documentNames?: Record<string, string | undefined> | null;
};

const formatPaymentData = (payment: WorkOrderPayment | undefined) => {
  return {
    ...payment,
    issueDate: payment?.issueDate ? new Date(payment?.issueDate) : undefined,
    checkNumber: payment?.checkNumber ? Number(payment?.checkNumber) : undefined,
    amount: Number(payment?.amount ?? 0),
  };
};

const getWorkOrderStatus = (serverTo?: WorkOrderServeTo, serverId?: string | null): ProcessStatus => {
  if (serverTo) {
    if (serverTo === WorkOrderServeTo.SERVER && serverId) return 'ASSIGNED';
    if (serverTo === WorkOrderServeTo.VENDOR) return 'ASSIGNED';
  }
  return 'AWAITING';
};

export const PEUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const loggedInUser = useUserStore((store) => store.user);

  const schemas = [ClientDetailsSchema, PeCaseDetailsSchema, PeWoDetailsSchema];
  const [searchParam, setSearchParam] = useSearchParams();
  const [steps, setSteps] = useState([
    { title: 'Client Details', isCompleted: true },
    { title: 'Case Details', isCompleted: true },
    { title: 'W/O Details', isCompleted: true },
  ]);
  const [previousPath, setPreviousPath] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [currentSchema, setCurrentSchema] = useState(schemas[activeStep]);
  const [services, setServices] = useState<
    { id: string; name: string; type: undefined | string; data: WorkOrderSchema | undefined }[]
  >([{ id: genUniqueId(), name: 'New Person', type: undefined, data: {} }]);
  const [isSessionBlocked, setIsSessionBlocked] = useState(false);
  const [sessionOwner, setSessionOwner] = useState<UserResponse | null>(null);
  const [sessionExpiresIn, setSessionExpiresIn] = useState<number | null>(null);

  const isSessionOwnedRef = useRef(false);
  /** Baseline HI update fingerprints per WO index from last successful load (skip unchanged PATCH). */
  const initialHiUpdateFingerprintByIndexRef = useRef<Record<number, string>>({});
  /** Avoid wiping dirty form on React Query refetches of the same service revision. */
  const loadedServiceRevisionRef = useRef<string | null>(null);

  const form = useForm({
    resolver: zodResolver(currentSchema),
    mode: 'all',
  });

  const {
    getValues,
    reset,
    formState: { isValid, isDirty },
  } = form;

  const { setIsDirty } = useContext(NavigationBlockerContext);

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  const { data: service, isFetching, isError } = useGetServiceById(id);

  const { mutate: updateService, status: serviceUpdateStatus } = useUpdateServiceById();
  const { mutateAsync: createHIWorkOrder, status: hiCreateStatus } = useCreateHIWorkOrder();
  const { mutateAsync: updateHIWorkOrder, status: hiUpdateStatus } = useUpdateHIWorkOrder();
  const [isSavingWithHi, setIsSavingWithHi] = useState(false);
  const [pendingLinkedHiByIndex, setPendingLinkedHiByIndex] = useState<PendingLinkedHiByIndex>({});
  const isSavePending =
    serviceUpdateStatus === 'pending' ||
    hiCreateStatus === 'pending' ||
    hiUpdateStatus === 'pending' ||
    isSavingWithHi;
  const [isAlertBoxOpen, setIsAlertBoxOpen] = useState(false);
  const { data: sessionData, isFetching: isCheckingSession } = useGetServiceSession(id);
  const { mutate: createServiceSession } = useCreateServiceSession(id);
  const { mutate: endSession } = useEndServiceSession(id);
  const { mutate: refreshSession } = useRefreshServiceSession(id);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const scrollToTop = () => {
    const toolbarElement = document.getElementById('toolbar');
    if (toolbarElement) {
      toolbarElement.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  };

  const buildServicePayload = (
    data: ServiceCreateUpdateRequest & {
      case?: ServiceCreateUpdateRequest['case'] & { attorneyName?: string | null };
      workOrders?: PsUpdateFormWorkOrder[];
    },
    linkedHiByIndex: Record<number, string | null | undefined>
  ): ServiceCreateUpdateRequest => {
    const rest = { ...data };
    const caseWithoutAttorneyName = { ...(rest.case ?? {}) };
    delete (caseWithoutAttorneyName as { attorneyName?: string | null }).attorneyName;

    const insuranceSource = caseWithoutAttorneyName.insurance;
    const insuranceClone = insuranceSource
      ? ({ ...insuranceSource } as typeof insuranceSource & { createdAt?: unknown; updatedAt?: unknown })
      : undefined;
    if (insuranceClone) {
      delete insuranceClone.createdAt;
      delete insuranceClone.updatedAt;
    }

    const mappedWorkOrders =
      data?.workOrders?.map((wo) => {
        const documentNames = (wo as { documentNames?: Record<string, string | undefined> | null }).documentNames ?? {};
        const woRest = omitFormOnlyWorkOrderFields(wo);

        return {
          ...woRest,
          phone: getPhoneNumberValue(wo.phone) ?? null,
          serverId: wo.serveTo === WorkOrderServeTo.SERVER ? wo.serverId : null,
          serverInstructions: wo.serverInstructions,
          status: getWorkOrderStatus(wo.serveTo, wo.serverId),
          privateInfo: wo.privateInfo
            ? {
                ssn: wo.privateInfo.ssn ?? undefined,
                dlNumber: wo.privateInfo.dlNumber ?? undefined,
                dateOfBirth: wo.privateInfo.dateOfBirth ?? undefined,
                others: wo.privateInfo.others ?? undefined,
              }
            : undefined,
          documents: Object.entries(wo.documents || {})
            .filter(([, value]) => value)
            .map(([id]) => ({
              caseDocTypeId: id,
              editedName: documentNames[id]?.trim() ? documentNames[id].trim() : null,
            })),
          vendorDetails:
            wo.serveTo === WorkOrderServeTo.VENDOR
              ? {
                  ...wo.vendorDetails,
                  amount: Number(wo?.vendorDetails?.amount),
                  otherAction: wo.vendorDetails?.action === Action.OTHER ? wo.vendorDetails.otherAction : null,
                  otherAfterAction: wo.vendorDetails?.afterAction?.includes(AfterAction.OTHER)
                    ? wo.vendorDetails.otherAfterAction
                    : null,
                }
              : null,
          payments: [
            ...(wo.payments?.map((payment) => ({
              ...payment,
              amount: Number(payment.amount ?? 0),
              paymentType: PaymentType.OPERATING_CHECK,
              checkNumber: payment.checkNumber ? Number(payment.checkNumber) : undefined,
            })) ?? []),

            ...(wo.hasWitnessFees && wo.witnessFee && caseTypesWithWitnessFees.includes(data.case.typeId)
              ? [
                  {
                    ...wo.witnessFee,
                    amount: Number(wo.witnessFee.amount ?? 0),
                    paymentType: PaymentType.WITNESS_FEE,
                    checkNumber: Number(wo.witnessFee.checkNumber),
                  },
                ]
              : []),
          ],
          teamId: wo.teamId,
          userId: wo.teamId ? wo.userId : undefined,
          ...(isPrepaidFeatureEnabled && {
            isPrepaid: data.isPrepaid,
          }),
        };
      }) ?? [];

    return {
      ...rest,
      case: {
        ...caseWithoutAttorneyName,
        insurance: insuranceClone
          ? {
              ...insuranceClone,
              phone: getPhoneNumberValue(insuranceClone.phone),
            }
          : undefined,
        address1: caseWithoutAttorneyName?.address1,
        address2: caseWithoutAttorneyName?.address2,
        city: caseWithoutAttorneyName?.city,
        state: caseWithoutAttorneyName?.state,
        zipCode: caseWithoutAttorneyName?.zipCode,
      },
      workOrders: applyLinkedHiWorkOrderIds(mappedWorkOrders, linkedHiByIndex),
    } as ServiceCreateUpdateRequest;
  };

  const saveHandler = async () => {
    const formValues = getValues();
    const data = formValues as ServiceCreateUpdateRequest & {
      case?: ServiceCreateUpdateRequest['case'] & { attorneyName?: string | null };
      workOrders?: PsUpdateFormWorkOrder[];
    };
    const workOrders = data.workOrders ?? [];

    const onPsSuccess = () => {
      setPendingLinkedHiByIndex({});
      toast({
        variant: 'default',
        description: 'Process service updated successfully',
        duration: 5000,
      });
      navigate(previousPath);
    };

    const nextUpdateFingerprints: Record<number, string> = {};
    const nextCreateFingerprints: Record<number, string> = {};
    workOrders.forEach((wo, index) => {
      if (!wo.includeHomeInspection) return;
      if (wo.linkedHIWorkOrderID) {
        nextUpdateFingerprints[index] = fingerprintHiUpdatePayload(mapPsFormToHiUpdatePayload(formValues, index));
      } else {
        nextCreateFingerprints[index] = fingerprintHiCreatePayload(mapPsFormToHiCreatePayload(formValues, index));
      }
    });

    const plan = planPsEditLinkedHiSave({
      workOrders,
      pendingByIndex: pendingLinkedHiByIndex,
      baselineUpdateFingerprints: initialHiUpdateFingerprintByIndexRef.current,
      nextUpdateFingerprints,
      nextCreateFingerprints,
    });

    const linkedHiByIndex = { ...plan.linkedHiByIndex };

    if (plan.abandonedPendingIds.length > 0) {
      toast({
        variant: 'default',
        description: `Home Inspection(s) ${plan.abandonedPendingIds.join(', ')} were created earlier but will not be linked because Include Home Inspection was unchecked.`,
        duration: 8000,
      });
    }

    if (plan.indexesNeedingHiApi.length === 0) {
      setPendingLinkedHiByIndex({});
      updateService(
        { serviceId: id, data: buildServicePayload(data, linkedHiByIndex) },
        {
          onSuccess: onPsSuccess,
          onError: (error) => {
            displayError(error, 5000);
          },
        }
      );
      return;
    }

    if (plan.needsNewHiCreate && !canCreateLinkedHomeInspection()) {
      toast({
        variant: 'destructive',
        description: 'Include Home Inspection is not available for your account or feature flags.',
        duration: 5000,
      });
      return;
    }

    setIsSavingWithHi(true);
    try {
      const nextPending: PendingLinkedHiByIndex = { ...pendingLinkedHiByIndex };

      for (const action of plan.actions) {
        if (action.kind === 'keep') {
          linkedHiByIndex[action.index] = action.id;
          continue;
        }

        if (action.kind === 'patch') {
          // REQ-3: edit-lock disables the checkbox via includeHomeInspectionDisabled.
          // Attempt/process-rule blocks are not on GET /services/{id} (spec OQ resolved) —
          // backend rejections surface via displayError / toast.
          const hiUpdatePayload = mapPsFormToHiUpdatePayload(formValues, action.index);
          const nextFingerprint = fingerprintHiUpdatePayload(hiUpdatePayload);
          const hiWorkOrder = await updateHIWorkOrder({ id: action.id, payload: hiUpdatePayload });
          linkedHiByIndex[action.index] = hiWorkOrder.workOrderId;
          initialHiUpdateFingerprintByIndexRef.current[action.index] = nextFingerprint;
          continue;
        }

        if (action.kind === 'reusePending') {
          linkedHiByIndex[action.index] = action.id;
          continue;
        }

        const hiPayload = mapPsFormToHiCreatePayload(formValues, action.index);
        const nextFingerprint = fingerprintHiCreatePayload(hiPayload);
        const pending = nextPending[action.index];

        if (pending && !shouldReusePendingLinkedHi(pending, nextFingerprint)) {
          toast({
            variant: 'default',
            description: `Form data changed for work order ${action.index + 1} since Home Inspection ${pending.id} was created. Creating a new Home Inspection.`,
            duration: 6000,
          });
        }

        const hiWorkOrder = await createHIWorkOrder(hiPayload);
        linkedHiByIndex[action.index] = hiWorkOrder.workOrderId;
        nextPending[action.index] = { id: hiWorkOrder.workOrderId, fingerprint: nextFingerprint };
      }

      Object.keys(nextPending).forEach((key) => {
        const index = Number(key);
        if (!plan.indexesNeedingHiApi.includes(index)) {
          delete nextPending[index];
        }
      });
      // Drop abandoned pending entries for unchecked WOs
      plan.abandonedPendingIds.forEach((abandonedId) => {
        Object.keys(nextPending).forEach((key) => {
          if (nextPending[Number(key)]?.id === abandonedId) {
            delete nextPending[Number(key)];
          }
        });
      });
      setPendingLinkedHiByIndex(nextPending);

      updateService(
        { serviceId: id, data: buildServicePayload(data, linkedHiByIndex) },
        {
          onSuccess: () => {
            setIsSavingWithHi(false);
            onPsSuccess();
          },
          onError: (error) => {
            setIsSavingWithHi(false);
            const hiSummary = Object.values(linkedHiByIndex).filter(Boolean).join(', ');
            toast({
              variant: 'destructive',
              description: hiSummary
                ? `Home Inspection(s) ${hiSummary} were saved, but Process Service failed: ${getPsSaveErrorMessage(error, 'Process Service update failed')}. Retrying Save will reuse unchanged Home Inspections.`
                : getPsSaveErrorMessage(error, 'Process Service update failed'),
              duration: 8000,
            });
          },
        }
      );
    } catch (error) {
      setIsSavingWithHi(false);
      displayError(error, 5000);
    }
  };

  useEffect(() => {
    const activeStep = searchParam.get('stepper');
    if (activeStep) {
      const index = steps.findIndex((step) => step.title === activeStep);
      setActiveStep(index);
    }
  }, [searchParam]);

  const goToStep = async (step: number) => {
    const isValid = await form.trigger();
    if (isValid) {
      if (steps[step].isCompleted || step <= activeStep) {
        const params = new URLSearchParams(searchParam);
        params.set('stepper', steps[step].title);
        setSearchParam(params);
        setActiveStep(step);
      }
    }
  };

  useEffect(() => {
    const searchParamWoId = searchParam.get('workOrderId');
    if (searchParamWoId) setPreviousPath(`/process?workOrderId=${searchParamWoId}`);
    else setPreviousPath('/process');
  }, []);

  useEffect(() => {
    setCurrentSchema(schemas[activeStep]);
    setTimeout(async () => {
      await form.trigger();
      form.clearErrors();
    }, 50);
    setTimeout(() => {
      scrollToTop();
    }, 100);
  }, [activeStep]);

  useEffect(() => {
    if (service?.data) {
      const serviceData = service?.data;
      const revisionKey = `${serviceData.serviceId}:${String(serviceData.updatedAt)}`;
      if (loadedServiceRevisionRef.current === revisionKey) {
        return;
      }
      loadedServiceRevisionRef.current = revisionKey;

      const attorneyDisplayName = serviceData?.case?.attorney
        ? displayFullName(serviceData.case.attorney)
        : null;
      const resetValues = {
        ...serviceData,
        case: {
          ...serviceData.case,
          serveBy: serviceData?.case?.serveBy ? new Date(serviceData?.case?.serveBy) : undefined,
          hearingOn: serviceData?.case?.hearingOn ? new Date(serviceData.case.hearingOn) : undefined,
          hasInsurance: !!serviceData?.case?.insurance,
          attorneyName: attorneyDisplayName && attorneyDisplayName !== '-' ? attorneyDisplayName : null,
          insurance: serviceData?.case?.insurance
            ? {
                ...serviceData.case.insurance,
                dateOfLoss: serviceData.case?.insurance?.dateOfLoss
                  ? new Date(serviceData.case.insurance.dateOfLoss)
                  : undefined,
              }
            : undefined,
        },
        workOrders: serviceData?.workOrders?.map((wo) => ({
          ...wo,
          serverId: wo.serverId ?? null,
          server: wo.server ?? null,
          serveTo: wo.serverId
            ? WorkOrderServeTo.SERVER
            : hasVendorAssignmentOnWorkOrder(wo)
              ? WorkOrderServeTo.VENDOR
              : null,
          privateInfo: wo.privateInfo
            ? {
                ...wo.privateInfo,
                dateOfBirth: wo.privateInfo.dateOfBirth ? new Date(wo.privateInfo.dateOfBirth) : undefined,
              }
            : undefined,
          hasPrivateInfo: !!wo.privateInfo,
          documents: Object.fromEntries(
            (wo.documents as { caseDocTypeId: string }[]).flatMap((doc) => [[doc.caseDocTypeId, true]])
          ),
          documentNames: Object.fromEntries(
            (wo.documents as { caseDocTypeId: string; editedName?: string | null }[])
              .filter((doc) => doc.editedName)
              .map((doc) => [doc.caseDocTypeId, doc.editedName!])
          ),
          vendorDetails: wo.vendor
            ? {
                department: wo.vendor.department,
                vendorId: wo.vendor.vendorId,
                vendorContactId: wo.vendor.vendorContactId,
                action: wo.vendor.action,
                otherAction: wo.vendor.otherAction,
                afterAction: wo.vendor.afterAction,
                otherAfterAction: wo.vendor.otherAfterAction,
                amount: String(wo.vendor.amount),
                trackingNumber: wo.vendor.trackingNumber,
              }
            : null,
          witnessFee: formatPaymentData(
            wo.workOrderPayments?.find((payment) => payment.paymentType === PaymentType.WITNESS_FEE)
          ),
          payments:
            wo.workOrderPayments
              ?.filter((payment) => payment.paymentType === PaymentType.OPERATING_CHECK)
              .map((payment) => formatPaymentData(payment)) ?? [],

          hasWitnessFees:
            wo.workOrderPayments?.some((payment) => payment.paymentType === PaymentType.WITNESS_FEE) ?? false,
          teamId: wo?.teamId,
          userId: wo?.user?.userId ?? null,
          includeHomeInspection: Boolean(wo.linkedHIWorkOrderID),
          includeHomeInspectionManualOverride: true,
        })),
        ...(isPrepaidFeatureEnabled && {
          isPrepaid: serviceData.workOrders?.[0]?.isPrepaid ?? false,
        }),
      };
      reset(resetValues);
      setPendingLinkedHiByIndex({});

      const fingerprints: Record<number, string> = {};
      resetValues.workOrders?.forEach((wo, index) => {
        if (wo.linkedHIWorkOrderID) {
          fingerprints[index] = fingerprintHiUpdatePayload(mapPsFormToHiUpdatePayload(resetValues, index));
        }
      });
      initialHiUpdateFingerprintByIndexRef.current = fingerprints;

      setServices(
        serviceData?.workOrders?.map((wo) => ({
          id: wo.workOrderId ?? genUniqueId(),
          name: wo.toServe ?? '',
          type: typeof wo.type === 'string' ? wo.type : undefined,
          data: wo as unknown as WorkOrderSchema,
        })) ?? []
      );
    }
  }, [service]);

  const checkAffidavitType = () => {
    if (!(getValues('workOrders') as WorkOrderSchema[])) return false;
    return (getValues('workOrders') as WorkOrderSchema[]).some((workOrder) => !workOrder.affidavitTypeId);
  };

  const nextStep = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      setSteps((prevSteps) =>
        prevSteps.map((step, index) => (index === activeStep ? { ...step, isCompleted: true } : step))
      );
      const params = new URLSearchParams(searchParam);
      params.set('stepper', steps[activeStep + 1].title);
      setSearchParam(params);
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };
  useEffect(() => {
    setCurrentSchema(schemas[activeStep]);
    setTimeout(async () => {
      await form.trigger();
      form.clearErrors();
      if (!Object.keys(form.formState.dirtyFields).length) {
        const currentValues = form.getValues();
        reset(currentValues, {
          keepValues: true,
          keepDirty: false,
        });
      }
    }, 50);
    setTimeout(() => {
      scrollToTop();
    }, 100);
  }, [activeStep]);

  useEffect(() => {
    if (isCheckingSession) return;

    const currentSession = sessionData?.data;

    const isLockedByOther = currentSession && currentSession.canEdit === false;

    const sessionOwnedByCurrentUser = currentSession?.user?.userId === loggedInUser?.userId;

    if (isLockedByOther) {
      setIsSessionBlocked(true);
      if (currentSession?.user) {
        setSessionOwner(currentSession.user);
      }
      isSessionOwnedRef.current = false;
    } else if (!sessionOwnedByCurrentUser) {
      createServiceSession(undefined, {
        onSuccess: (data) => {
          setIsSessionBlocked(false);
          isSessionOwnedRef.current = true;
          setSessionExpiresIn(data.sessionExpireInMs ?? null);
        },
        onError: () => {
          setIsSessionBlocked(true);
          isSessionOwnedRef.current = false;
        },
      });
    }
    if (sessionOwnedByCurrentUser) {
      isSessionOwnedRef.current = true;
      setSessionExpiresIn(currentSession?.sessionExpireInMs ?? null);
    }
  }, [id, isCheckingSession, sessionData, loggedInUser?.userId]);

  useEffect(() => {
    return () => {
      if (isSessionOwnedRef.current) {
        endSession();
      }
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isSessionOwnedRef.current) {
        endSession();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!sessionExpiresIn || !isSessionOwnedRef.current) return;

    const timeUntilRefresh = Math.max(1000, sessionExpiresIn - 2000);

    const refreshTimer = setTimeout(() => {
      refreshSession(undefined, {
        onSuccess: (data) => {
          setSessionExpiresIn(data.sessionExpireInMs ?? null);
          isSessionOwnedRef.current = true;

          setRefreshTrigger((prev) => prev + 1);
        },
      });
    }, timeUntilRefresh);

    return () => clearTimeout(refreshTimer);
  }, [sessionExpiresIn, refreshTrigger]);

  return (
    <NotFoundRedirectionWrapper isItemNotFound={isError} itemName="service">
      <Loader isLoading={isFetching}>
        <div className="w-full scrollbar-hide">
          <div className="w-full">
            <ToolBar title="Process Service Update" />
            <div className="mt-3 flex justify-center pl-24">
              <Stepper steps={steps} activeStep={activeStep} goToStep={goToStep} />
            </div>

            <FormProvider {...form}>
              {activeStep === 0 && <PeClientDetails />}
              {activeStep === 1 && <PeCaseDetails mode="UPDATE" existingCaseDetails={service?.data.case} />}
              {activeStep === 2 && (
                <PeWoDetails
                  services={services}
                  setServices={setServices}
                  initialActiveWorkOrderId={searchParam.get('workOrderId') ?? undefined}
                  showIncludeHomeInspection
                  includeHomeInspectionDisabled={isSessionBlocked}
                />
              )}
            </FormProvider>

            <div className="flex flex-col items-end gap-2 mt-4 mb-8 w-full">
              <div className={`flex justify-end w-full gap-2`}>
                <AlertBox
                  triggerButton={
                    <Button
                      variant="outline"
                      className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF] "
                    >
                      Cancel
                    </Button>
                  }
                  title="Are you sure you want to cancel?"
                  description="All of your current changes will be lost."
                  warning={true}
                  cancelBtnLabel="No"
                  confirmBtnLabel="Yes"
                  confirmBtnClick={() => {
                    navigate(previousPath);
                  }}
                />
                {activeStep !== steps.length - 1 && checkAffidavitType() ? (
                  <Button
                    className="text-[#fff] py-2 px-4 rounded-md bg-primary  w-[125px] h-[48px] border-primary flex justify-center  hover:bg-[#12375C] hover:text-white disabled:bg-primary "
                    onClick={nextStep}
                    disabled={!form.formState.isValid}
                  >
                    Next
                  </Button>
                ) : (
                  <AlertBox
                    triggerButton={
                      <Button
                        variant="outline"
                        className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white "
                        disabled={!isValid || !isDirty || isSavePending || checkAffidavitType()}
                        onClick={() => setIsAlertBoxOpen(true)}
                      >
                        Save
                      </Button>
                    }
                    title="Are you sure?"
                    open={isAlertBoxOpen}
                    description="Would you like to update the process service’s details?"
                    cancelBtnLabel="No"
                    confirmBtnLabel="Yes"
                    cancelBtnClick={() => setIsAlertBoxOpen(false)}
                    isLoading={isSavePending}
                    confirmBtnClick={saveHandler}
                  />
                )}
              </div>
              {!(activeStep !== steps.length - 1 && checkAffidavitType()) && !isValid && (
                <FormProvider {...form}>
                  <PsSaveValidationMessage schema={currentSchema} />
                </FormProvider>
              )}
            </div>
          </div>
        </div>
      </Loader>
      {isSessionBlocked && (
        <AlertBox
          title="Service Is Being Edited"
          description={`${displayFullName(sessionOwner ?? {})} is currently editing this service. You cannot make changes until they are finished.`}
          confirmBtnLabel="Okay"
          confirmBtnClick={() => navigate(-1)}
          displayCancelButton={false}
          open={true}
        />
      )}
    </NotFoundRedirectionWrapper>
  );
};
